/**
 * A country.
 */
export interface Country {
  readonly id: string; // id in this case is a literal unique country code (same as 'code')
  readonly code: string;
  readonly name: string;
  /** only present when fetching the `countries` collection */
  readonly phoneCode?: number;
  /** only present when fetching a user's location, e.g. `projectViewUsers` */
  readonly flagUrl?: string;
  readonly sanction?: boolean;
}

export function isCountry(country: Partial<Country>): country is Country {
  return (
    country.id !== undefined &&
    country.code !== undefined &&
    country.name !== undefined
  );
}

export enum CountryCode {
  AF = 'AF',
  AR = 'AR',
  AU = 'AU',
  BD = 'BD',
  BR = 'BR',
  BS = 'BS',
  CA = 'CA',
  CL = 'CL',
  CN = 'CN',
  FR = 'FR',
  DE = 'DE',
  HK = 'HK',
  IN = 'IN',
  ID = 'ID',
  IR = 'IR',
  IQ = 'IQ',
  IE = 'IE',
  IL = 'IL',
  IT = 'IT',
  JP = 'JP',
  KR = 'KR',
  MY = 'MY',
  MX = 'MX',
  NL = 'NL',
  NZ = 'NZ',
  PK = 'PK',
  PH = 'PH',
  PL = 'PL',
  PT = 'PT',
  QA = 'QA',
  RU = 'RU',
  SG = 'SG',
  ZA = 'ZA',
  ES = 'ES',
  SE = 'SE',
  CH = 'CH',
  TW = 'TW',
  TH = 'TH',
  TR = 'TR',
  GB = 'GB',
  US = 'US',
  VN = 'VN',
}

type Countries = {
  readonly [C in CountryCode]: Country;
};

export const countries = Object.fromEntries(
  [
    {
      code: CountryCode.AF,
      name: 'Afghanistan',
      phoneCode: 93,
      sanction: false,
    },
    { code: CountryCode.AR, name: 'Argentina', phoneCode: 54, sanction: false },
    { code: CountryCode.AU, name: 'Australia', phoneCode: 61, sanction: false },
    {
      code: CountryCode.BD,
      name: 'Bangladesh',
      phoneCode: 880,
      sanction: false,
    },
    { code: CountryCode.BR, name: 'Brazil', phoneCode: 55, sanction: false },
    { code: CountryCode.BS, name: 'Bahamas', phoneCode: 1 },
    { code: CountryCode.CA, name: 'Canada', phoneCode: 1, sanction: false },
    { code: CountryCode.CL, name: 'Chile', phoneCode: 56, sanction: false },
    { code: CountryCode.CN, name: 'China', phoneCode: 86, sanction: false },
    { code: CountryCode.FR, name: 'France', phoneCode: 33, sanction: false },
    { code: CountryCode.DE, name: 'Germany', phoneCode: 49, sanction: false },
    {
      code: CountryCode.HK,
      name: 'Hong Kong',
      phoneCode: 852,
      sanction: false,
    },
    { code: CountryCode.IN, name: 'India', phoneCode: 91, sanction: false },
    { code: CountryCode.ID, name: 'Indonesia', phoneCode: 62, sanction: false },
    {
      code: CountryCode.IR,
      name: 'Iran, Islamic Republic of',
      phoneCode: 98,
      sanction: true,
    },
    { code: CountryCode.IQ, name: 'Iraq', phoneCode: 964, sanction: false },
    { code: CountryCode.IE, name: 'Ireland', phoneCode: 353, sanction: false },
    { code: CountryCode.IL, name: 'Israel', phoneCode: 972, sanction: false },
    { code: CountryCode.IT, name: 'Italy', phoneCode: 39, sanction: false },
    { code: CountryCode.JP, name: 'Japan', phoneCode: 81, sanction: false },
    {
      code: CountryCode.KR,
      name: 'Korea, Republic of',
      phoneCode: 82,
      sanction: false,
    },
    { code: CountryCode.MY, name: 'Malaysia', phoneCode: 60, sanction: false },
    { code: CountryCode.MX, name: 'Mexico', phoneCode: 52, sanction: false },
    {
      code: CountryCode.NL,
      name: 'Netherlands',
      phoneCode: 31,
      sanction: false,
    },
    {
      code: CountryCode.NZ,
      name: 'New Zealand',
      phoneCode: 64,
      sanction: false,
    },
    { code: CountryCode.PK, name: 'Pakistan', phoneCode: 92, sanction: false },
    {
      code: CountryCode.PH,
      name: 'Philippines',
      phoneCode: 63,
      sanction: false,
    },
    { code: CountryCode.PL, name: 'Poland', phoneCode: 48, sanction: false },
    { code: CountryCode.PT, name: 'Portugal', phoneCode: 351, sanction: false },
    { code: CountryCode.QA, name: 'Qatar', phoneCode: 974, sanction: false },
    {
      code: CountryCode.RU,
      name: 'Russian Federation',
      phoneCode: 7,
      sanction: true,
    },
    { code: CountryCode.SG, name: 'Singapore', phoneCode: 65, sanction: false },
    {
      code: CountryCode.ZA,
      name: 'South Africa',
      phoneCode: 27,
      sanction: false,
    },
    { code: CountryCode.ES, name: 'Spain', phoneCode: 34, sanction: false },
    { code: CountryCode.SE, name: 'Sweden', phoneCode: 46, sanction: false },
    {
      code: CountryCode.CH,
      name: 'Switzerland',
      phoneCode: 41,
      sanction: false,
    },
    { code: CountryCode.TW, name: 'Taiwan', phoneCode: 886, sanction: false },
    { code: CountryCode.TH, name: 'Thailand', phoneCode: 66, sanction: false },
    { code: CountryCode.TR, name: 'Turkey', phoneCode: 90, sanction: false },
    {
      code: CountryCode.GB,
      name: 'United Kingdom',
      phoneCode: 44,
      sanction: false,
    },
    {
      code: CountryCode.US,
      name: 'United States',
      phoneCode: 1,
      sanction: false,
    },
    { code: CountryCode.VN, name: 'Vietnam', phoneCode: 84, sanction: false },
  ].map(({ code, name, phoneCode, sanction = false }) => [
    code,
    { id: code, code, name, phoneCode, sanction },
  ]),
) as Countries;
